/**
 * 수입조정결의 PoC
 * - 승인된 수입결의 선택 · 조정(+/-) 행 CRUD · 승인요청/승인
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  STATUS,
  STATUS_OPTIONS,
  createAdjustUid,
  emptyAdjustRow,
  loadAdjusts,
  saveAdjusts,
  filterAdjustsByResolution,
  loadResolutionBundle,
  requestAdjustApproval,
  approveAdjust,
  statusLabel,
  todayYmd,
} from 'income/incomeStore';

const GRID_ID = 'income-adjust';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

export default function IncomeAdjustWorkspace() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      fiscalYear: getCurrentFiscalYear(),
      resolutionPicker: '',
    },
  });
  const { getValues, reset, setValue } = methods;

  const gridRef = useRef(null);
  const [resolutionOptions, setResolutionOptions] = useState([]);
  const [selectedResolutionUid, setSelectedResolutionUid] = useState('');

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '조정일', field: 'adjustDate', size: 100, align: 'center' }),
      hiCol.edTextColumn({ header: '사유', field: 'reason', size: 160, editable: true }),
      hiCol.edNumberColumn({
        header: '조정금액',
        field: 'adjustAmt',
        size: 110,
        align: 'right',
        editable: true,
        title: '양수=증액 · 음수=감액',
      }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '전표', field: 'slipYn', size: 60, align: 'center' }),
      hiCol.roTextColumn({ header: '등록일', field: 'registerDate', size: 100, align: 'center' }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyAdjustRow({ adjustDate: todayYmd(), mod: 'A' }),
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const loadApprovedResolutions = useCallback(async () => {
    const d = dims();
    const bundle = await loadResolutionBundle(d);
    const opts = (bundle.masters || [])
      .filter((m) => m.mod !== 'D' && m.status === STATUS.APPROVED)
      .map((m) => ({
        value: m.resolutionUid,
        label: `${m.resolutionNo} · ${m.deptName || ''} · ${Number(m.resolutionAmt || 0).toLocaleString()}원`,
        ...m,
      }));
    setResolutionOptions(opts);
    return opts;
  }, [dims]);

  const loadAdjustGrid = useCallback(
    async (resolutionUid) => {
      const d = dims();
      const all = await loadAdjusts(d);
      const rows = filterAdjustsByResolution(all, resolutionUid).map((r) => ({
        ...r,
        statusNm: statusLabel(r.status),
      }));
      gridRef.current?.setData?.(rows);
    },
    [dims]
  );

  const handleSearch = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const opts = loadApprovedResolutions();
    const picker = String(getValues('resolutionPicker') || '').trim();
    const uid = picker || opts[0]?.value || '';
    setSelectedResolutionUid(uid);
    if (uid) setValue('resolutionPicker', uid);
    loadAdjustGrid(uid);
  }, [dims, getValues, loadApprovedResolutions, loadAdjustGrid, setValue]);

  useEffect(() => {
    reset({ fiscalYear: getCurrentFiscalYear(), resolutionPicker: '' });
  }, [reset]);

  const onResolutionPick = useCallback(
    (e) => {
      const val = e?.target?.value ?? e;
      setSelectedResolutionUid(val);
      loadAdjustGrid(val);
    },
    [loadAdjustGrid]
  );

  const handleAdd = useCallback(async () => {
    if (!selectedResolutionUid) {
      showAlert('수입결의를 먼저 선택하세요.');
      return;
    }
    const res = resolutionOptions.find((o) => o.value === selectedResolutionUid);
    const row = emptyAdjustRow({
      adjustUid: createAdjustUid(),
      incomeResolutionUid: selectedResolutionUid,
      incomeResolutionNo: res?.resolutionNo || '',
      adjustDate: todayYmd(),
      registerDate: todayYmd(),
      status: STATUS.DRAFT,
      mod: 'A',
    });
    gridRef.current?.setData?.([...(gridRef.current?.getData?.() || []), row]);
  }, [selectedResolutionUid, resolutionOptions]);

  const persistGrid = useCallback(async () => {
    const d = dims();
    const uid = selectedResolutionUid;
    if (!uid) {
      showAlert('수입결의를 선택하세요.');
      return false;
    }
    const gridRows = gridRef.current?.getData?.() || [];
    const all = await loadAdjusts(d);
    const other = all.filter((r) => r.incomeResolutionUid !== uid);
    const res = resolutionOptions.find((o) => o.value === uid);

    const merged = gridRows.map((row) => {
      const adjustUid = row.adjustUid || createAdjustUid();
      const prev = all.find((a) => a.adjustUid === adjustUid);
      return {
        ...emptyAdjustRow(),
        ...(prev || {}),
        ...row,
        adjustUid,
        incomeResolutionUid: uid,
        incomeResolutionNo: res?.resolutionNo || row.incomeResolutionNo || '',
        registerDate: row.registerDate || prev?.registerDate || todayYmd(),
        mod: prev ? (row.mod === 'D' ? 'D' : 'U') : row.mod || 'A',
      };
    });

    await saveAdjusts(d, [...other, ...merged]);
    return true;
  }, [dims, selectedResolutionUid, resolutionOptions]);

  const handleSave = useCallback(async () => {
    const ok = await persistGrid();
    if (!ok) return;
    await handleSearch();
    showAlert('저장했습니다.', 'success');
  }, [persistGrid, handleSearch]);

  const handleRequest = useCallback(async () => {
    const rows = gridRef.current?.getData?.() || [];
    const sel = gridRef.current?.getSelectedRow?.();
    const target = sel || rows.find((r) => r.status === STATUS.DRAFT);
    if (!target?.adjustUid) {
      showAlert('승인요청할 조정행을 선택하세요.');
      return;
    }
    if (target.status !== STATUS.DRAFT && target.status !== STATUS.REJECTED) {
      showAlert('작성중·반려 상태만 승인요청할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await persistGrid();
    const d = dims();
    const all = await loadAdjusts(d);
    const next = requestAdjustApproval(all, target.adjustUid);
    await saveAdjusts(d, next);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [persistGrid, dims, handleSearch]);

  const handleApprove = useCallback(async () => {
    const rows = gridRef.current?.getData?.() || [];
    const sel = gridRef.current?.getSelectedRow?.();
    const target = sel || rows.find((r) => r.status === STATUS.REQUESTED);
    if (!target?.adjustUid) {
      showAlert('승인할 조정행을 선택하세요.');
      return;
    }
    if (target.status !== STATUS.REQUESTED) {
      showAlert('승인요청 상태만 승인할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인할까요? (전표 stub)');
    if (!ok) return;
    const d = dims();
    const all = await loadAdjusts(d);
    const next = approveAdjust(all, target.adjustUid);
    await saveAdjusts(d, next);
    await handleSearch();
    showAlert('승인했습니다.', 'success');
  }, [dims, handleSearch]);

  const searchFields = useMemo(
    () => [
      [
        { name: 'fiscalYear', label: '회계연도', type: 'fiscalYear', width: '88px', labelWidth: SEARCH_LABEL_W },
        {
          name: 'resolutionPicker',
          label: '수입결의',
          type: 'select',
          options: [{ value: '', label: '(선택)' }, ...resolutionOptions.map((o) => ({ value: o.value, label: o.label }))],
          width: '280px',
          labelWidth: SEARCH_LABEL_W,
          onChange: onResolutionPick,
        },
      ],
    ],
    [resolutionOptions, onResolutionPick]
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleAdd, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: () => grid.handleDelete?.(),
        hotkey: 'F9',
        variant: 'danger',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    ],
    [handleSearch, handleAdd, handleSave, grid]
  );

  const extraButtons = useMemo(
    () => [
      { id: 'request', label: '승인요청', onClick: handleRequest },
      { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
    ],
    [handleRequest, handleApprove]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel storageKey="income-adjust" prefsUserId={prefsUserId} title="조회조건">
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>승인된 수입결의별 조정(+/-) · 승인 시 slipYn=Y 전표 stub</Hint>

          <Panel>
            <GridBody>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableContextMenu
                enableRowEditMenu
                enableCut
                enableFindReplace
                allowReplace
                enableHeaderMenu
                enableSelectionStats
                enableQuickExport
                exportFileName="수입조정"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                {...(grid.contextMenuProps || {})}
              />
            </GridBody>
          </Panel>
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}
